import { useEffect, useState } from "react";
import { fileUploadApiKeyStore } from "../../../../../api/file-upload-key-store";
import { gridApiKeyStore } from "../../../../../api/grid-api-key-store";
import { SiTrackGrid } from "../../../../../components/si-track-grid/si-track-grid";
import { useLoadData } from "../../../../../hooks/useLoadData";
import { useSaveData } from "../../../../../hooks/useSaveData";
import { ApiResponse } from "../../../../../models/api-response";
import { UserPermissionName } from "../../../../../models/user-permission-name";
import { GridColumnConfig, GridPreference } from "../../../../../models/gridCoumnConfig";
import { State } from "@progress/kendo-data-query";
import useDebounce from "../../../../../hooks/use-debounce";
import { fileColumns } from "./grid-columns";
import { useFileDownload } from "../../../../../components/file-download";
import { Button, Checkbox, Grid, TextField, Typography } from "@mui/material";
import { GuardedComponent } from "../../../../../components/guarded-component";
import { FileUploadDialog } from "./file-upload.dialog";
import { useSnackbar } from "../../../../../hooks/use-snackbar.hook";

interface CellProps {
    moduleData: any;
}

interface CustomGridCellProps {
    field: string;
}

interface FileUploadComponent {
    title: string;
    fileOrders: any;
    planReviewTaskData: any;
    parentLoading: boolean;
    selectedFileData: (files: any) => void;
}

export const FileUploadComponent = ({ title, fileOrders, planReviewTaskData, parentLoading, selectedFileData }: FileUploadComponent) => {

    const [editItem, setEditItem] = useState<any>();
    const [updatedFileData, setFileData] = useState<any>();

    const { showSnackbar } = useSnackbar();

    const { downloadFile, isLoading } = useFileDownload();

    const { isLoading: isFileUploadLoading, isFetching: isFileUploadFetching, data: UnitDetailFiles, refetch
    } = useLoadData<ApiResponse<any[]>>(fileUploadApiKeyStore.getFilesForPlanReview({ planReviewTaskID: planReviewTaskData?.planReviewTaskID, unitID: planReviewTaskData?.unitID }));

    useEffect(() => {
        if (UnitDetailFiles) {
            const updatedData = UnitDetailFiles?.data?.map((cd: any) => ({
                ...cd,
                isSelected: cd?.isSelected,
            }));
            const uniqueData = updatedData?.filter(
                (file, index, self) =>
                    index === self.findIndex(f => f.fileID === file.fileID)
            );
            setFileData(uniqueData);
            selectedFileData(uniqueData?.filter((con: any) => con?.isSelected == true));
        }
    }, [UnitDetailFiles, parentLoading]);


    const handleDialogClosed = () => { setEditItem(undefined); }

    const handleUploadFile = () => { setEditItem({}); }

    const handleEditFile = (value: any) => {
        setEditItem(value);
    };

    const handleDownloadFile = async (data: any) => {
        downloadFile({ additionalData: data });
    };

    const allowedTypes = [".pdf", "pdf", "application/pdf"];

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>, moduleData: any) => {
        if (event.target.checked && !allowedTypes.includes(moduleData?.fileType)) {
            showSnackbar(`Only pdf can be sent in mail.`, "error");
            return;
        }

        const updatedData = updatedFileData?.map((d: any) => ({
            ...d,
            isSelected: (moduleData?.fileID == d?.fileID) ? event.target.checked : d?.isSelected,
            fileOrder: (moduleData?.fileID == d?.fileID) ?
                UnitDetailFiles?.data?.find((f: any) => moduleData?.fileID == f?.fileID)?.fileOrder ?? 0
                : d?.fileOrder,
        }));
        setFileData(updatedData);
        selectedFileData(updatedData?.filter((con: any) => con?.isSelected == true));
    };

    const SelectContactCell = ({ moduleData }: CellProps) => {
        return (
            <td>
                <Checkbox
                    checked={updatedFileData?.find((pl: any) => pl.fileID == moduleData?.fileID)?.isSelected}
                    onChange={(e) => handleChange(e, moduleData)}
                    inputProps={{ 'aria-label': 'controlled' }}
                />
            </td>
        );
    };

    const checkFileOrderValue = (fileData: any, newValue: any) => {
        const isFileOrderUsed = fileData?.some(
            (file: any) =>
                file?.fileOrder !== null && Number(file?.fileOrder) === Number(newValue?.fileOrder) && file?.fileID !== newValue?.fileID
        );
        return isFileOrderUsed
    }

    const handleOrderChange = (moduleData: any, fileValue: any) => {

        if (fileOrders?.includes(Number(fileValue))) {
            showSnackbar(`This file order has already been assigned to an plan.`, 'error');
            return;
        }

        if (checkFileOrderValue(updatedFileData, { fileID: moduleData?.fileID, fileOrder: fileValue })) {
            showSnackbar(`This file order has already been assigned to the another file.`, 'error');
            return;
        }

        const updatedData = updatedFileData?.map((d: any) => ({
            ...d,
            fileOrder: (moduleData?.fileID == d?.fileID) ? fileValue : d?.fileOrder
        }));
        setFileData(updatedData);
        selectedFileData(updatedData?.filter((con: any) => con?.isSelected == true));
    }

    const FileOrderCell = ({ moduleData }: CellProps) => {
        const isFileSelected = moduleData?.isSelected;
        const [fileOrderValue, setFileOrderValue] = useState<any>(moduleData?.fileOrder);

        const handleChange = (event: any) => {
            setFileOrderValue(event?.target?.value)
        }

        return (
            <td>
                {isFileSelected ?
                    <TextField
                        hiddenLabel
                        type="number"
                        id="filled-hidden-label-small"
                        variant="outlined"
                        size="small"
                        value={fileOrderValue}
                        onChange={handleChange}
                        onBlur={(e) => handleOrderChange(moduleData, fileOrderValue)}
                        onInput={(e: any) => {
                            if (e.target.value < 0) e.target.value = 0;
                        }}
                        inputProps={{ min: 0 }}
                    />
                    : <Typography variant="body1">{moduleData?.fileOrder}</Typography>
                }
            </td>
        );
    };

    const CustomGridCell = ({ field }: CustomGridCellProps) => {
        if (field === 'select') {
            return (dataItem: any) => {
                return <SelectContactCell moduleData={dataItem.dataItem} />;
            };
        }
        if (field === 'fileOrder') {
            return (dataItem: any) => {
                return <FileOrderCell moduleData={dataItem.dataItem} />;
            };
        }
    }

    // -------------------------------------------------Grid Layout------------------------------
    const titleName = title?.split(" ")?.join("");
    const {
        isLoading: isLoadingGridLayout,
        data: gridLayouts,
        refetch: refetchGridLayout
    } = useLoadData<ApiResponse<GridPreference>>(
        gridApiKeyStore.getGridLayout(`${titleName}PlanReviewFile`, `${titleName}PlanReviewFile`)
    );

    const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
    const [gridConfig, setGridConfig] = useState<{
        dataState: State | undefined;
        columns: any[] | undefined;
    }>();

    const [filteredUnitDetailFiles, setFilteredUnitDetailFiles] = useState<any[]>([]);

    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    useEffect(() => {
        const filteredUnitDetailFileList = updatedFileData?.filter((UnitDetailFile: any) =>
            Object.values(UnitDetailFile).some(
                (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
            )
        );
        setFilteredUnitDetailFiles(filteredUnitDetailFileList || []);
    }, [debouncedSearchTerm, updatedFileData]);

    const handleInputChange: any = (value: string) => {
        setSearchTerm(value);
    };

    const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
        const data = {
            filterJSON: JSON.stringify(gridDataState),
            columnJSON: JSON.stringify(gridColumns),
            gridName: `${titleName}PlanReviewFile`,
            pageName: `${titleName}PlanReviewFile`,
        };

        mutateAsync({ data }).then(() => {
            refetchGridLayout();
        });
    };

    const handleResetLayout = () => {
        const data = {
            filterJSON: null,
            columnJSON: null,
            gridName: `${titleName}PlanReviewFile`,
            pageName: `${titleName}PlanReviewFile`,
        };

        mutateAsync({ data }).then(() => {
            refetchGridLayout();
        });
    };

    useEffect(() => {
        if (!gridLayouts?.data) return;

        try {
            const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

            const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

            // if (filterData && columnJson) {
            setGridConfig({
                columns: columnJson,
                dataState: filterData,
            });
            // }
        } catch (error) {
            console.log('Error parsing grid layout JSON:', error);
        }
    }, [gridLayouts?.data]);

    // ----------------------------------------------------------------------------------------
    return (
        <Grid container spacing={1}>
            <Grid className="subHead" item lg={12} sm={12} xl={12} xs={12}>
                <Typography variant="h3" color="textPrimary">
                    UPLOADED FILES
                </Typography>
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
                <SiTrackGrid
                    fileTitle='plan_review_file'
                    dataItemKey="fileID"
                    currentPageSize={5}
                    columns={fileColumns}
                    customCell={(field) =>
                        CustomGridCell({
                            field,
                        })
                    }
                    onDownload={(data: any) => {
                        handleDownloadFile(data);
                    }}
                    onEdit={handleEditFile}
                    deleteMutationConfig={(item) => fileUploadApiKeyStore.deleteFile(item.fileID)}
                    actionPermissions={{
                        edit: UserPermissionName.EDIT_FILE,
                        download: UserPermissionName.DOWNLOAD_FILE,
                        export: UserPermissionName.DOWNLOAD_GRID
                    }}
                    isBusy={isLoading || isFileUploadLoading || isFileUploadFetching || isLoadingGridLayout || parentLoading}

                    // -----------------Grid Layout-------
                    dataItems={filteredUnitDetailFiles || []}
                    reorderable
                    handleSearch={handleInputChange}
                    handleSaveLayout={handleSaveLayout}
                    isSaveLayout={true}
                    gridConfigFromApi={gridConfig}
                    handleResetLayout={handleResetLayout}
                // -----------------Grid Layout-------
                />
            </Grid>
            <GuardedComponent requiredPermissions={[UserPermissionName.UPLOAD_FILE]}>
                <Grid item lg={12} sm={12} xl={12} xs={12}>
                    <Grid
                        container
                        direction="row"
                        sx={{
                            justifyContent: "flex-end",
                            alignItems: "flex-start",
                        }}
                    >
                        <Button variant="contained" style={{ width: '130px' }} onClick={handleUploadFile}>
                            ADD FILES
                        </Button>
                    </Grid>
                </Grid>
            </GuardedComponent>
            {editItem && (
                <FileUploadDialog
                    planReviewTaskData={planReviewTaskData}
                    fileData={editItem}
                    onClosed={handleDialogClosed} />
            )}
        </Grid>
    );
}